import { useAppSelector, useTranslate } from '@src/core/hooks';
import { SaleFormInput, FormInputInputType } from '@src/core/buyerApiClient';
import React, { useMemo } from 'react';
import { device } from '@src/common/breakpoints';
import { BodyExtraSmallMedium, BodySmallMedium } from '@src/fonts/typography/Typography';
import styled from 'styled-components';
import { PhoneInput } from '@planne-software/uni/PhoneInput';
import { ScopedCssBaseline } from '@planne-software/uni/mui/material';
import { ContainerSubtitleText } from '@src/features/orders/form-inputs/legacy/TariffExtraInfoCollect';
import { orderRequiredFormInputsSelector, RequiredFormInputsErrors } from '@src/features/orders';
import { resolveFormInputErrorMessage, type FormInputValue } from '@src/domains/orders';
import { resolveFormInputPlaceholder } from '@src/features/orders/form-inputs';
import { LayoutColors } from '@src/core/theme';

export const TariffExtraInfoFormInputs = ({ index, formInputs, handleInputDataToSend }: FormContainerProps) => {
  const { i18nElement } = useTranslate();

  const orderRequiredFormInputs = useAppSelector(orderRequiredFormInputsSelector);
  const saleRequiredFormInputs = useAppSelector(orderRequiredFormInputsSelector);
  const finalRequiredFormInputs = useMemo(
    () => orderRequiredFormInputs ?? saleRequiredFormInputs,
    [orderRequiredFormInputs, saleRequiredFormInputs],
  );

  return (
    <FormContainer>
      <FormHeader>
        <ContainerSubtitleText>
          {i18nElement('checkout_tariff')} {index + 1} - {formInputs[0]?.saleTariff?.name}
        </ContainerSubtitleText>
      </FormHeader>

      <InputsGrid>
        {formInputs.map((input) => (
          <InputContainer key={input.hash}>
            <InputLabel>{input.formInput.name}</InputLabel>

            {input.inputType === FormInputInputType.Phone ? (
              <PreparedPhoneInput
                {...{ input, errors: finalRequiredFormInputs?.errors ?? {}, handleInputDataToSend }}
              />
            ) : (
              <PreparedInput {...{ input, errors: finalRequiredFormInputs?.errors ?? {}, handleInputDataToSend }} />
            )}
            {finalRequiredFormInputs?.errors[input.hash]
              ? finalRequiredFormInputs?.errors[input.hash]?.map((errorMessage, index) => (
                  <ErrorLabel key={`error-label-${index}`}>
                    {resolveFormInputErrorMessage(errorMessage, input.hash, input.formInput.name)}
                  </ErrorLabel>
                ))
              : null}
          </InputContainer>
        ))}
      </InputsGrid>
    </FormContainer>
  );
};

const PreparedInput = ({ input, errors, handleInputDataToSend }: PreparedInputProps) => {
  const { i18n } = useTranslate();
  const placeholderKey = resolveFormInputPlaceholder(input.validationRules ?? null);

  return (
    <Input
      placeholder={placeholderKey ? i18n(placeholderKey) : ''}
      error={!!errors[input.hash]}
      type={input.inputType}
      onChange={(e) => {
        handleInputDataToSend({ value: e.target.value, hash: input.hash, inputType: input.inputType });
      }}
    />
  );
};

const PreparedPhoneInput = ({ input, errors, handleInputDataToSend }: PreparedInputProps) => {
  const { i18n } = useTranslate();
  const placeholderKey = resolveFormInputPlaceholder(input.validationRules ?? null);

  return (
    <ScopedCssBaseline>
      <PhoneInput
        name={input.hash}
        size='medium'
        placeholder={placeholderKey ? i18n(placeholderKey) : undefined}
        error={!!errors[input.hash]}
        onChange={({ phone, inputValue }) => {
          // An empty field is reported as the dial code alone (`+55`), which would count as filled.
          handleInputDataToSend({ value: inputValue ? phone : '', hash: input.hash, inputType: input.inputType });
        }}
      />
    </ScopedCssBaseline>
  );
};

interface PreparedInputProps {
  handleInputDataToSend: (inputData: FormInputValue) => void;
  input: SaleFormInput;
  errors: RequiredFormInputsErrors;
}

interface FormContainerProps {
  handleInputDataToSend: (inputData: FormInputValue) => void;
  formInputs: SaleFormInput[];
  index: number;
}

const FormContainer = styled.div`
  display: flex;
  flex-flow: column;
  gap: 16px;

  ${device.mediumTablet} {
    gap: 32px;
  }
`;

const FormHeader = styled.div`
  flex-flow: column;
  align-items: flex-start;

  ${device.smallTablet} {
    display: flex;
    flex-flow: row;
    align-items: center;
    justify-content: space-between;
  }
`;

const InputsGrid = styled.div`
  display: grid;
  grid-template-columns: 100%;
  gap: 16px;

  ${device.smallTablet} {
    display: grid;
    grid-template-columns: repeat(2, calc(50% - 8px));
    gap: 32px 16px;
  }
`;

const InputContainer = styled.div`
  display: flex;
  flex-flow: column;
  column-gap: 8px;
`;

const InputLabel = styled.div`
  ${BodySmallMedium};
  margin-bottom: 8px;
`;

const Input = styled.input<InputProps>`
  background-color: #ffffff;
  border: 1px solid ${(props) => (props.error ? LayoutColors.errorDark : '#949494')};
  border-radius: 4px;
  height: 46px;
  width: 100%;
  ${BodyExtraSmallMedium};
  padding: 12px;

  &::placeholder {
    opacity: 1;
    color: #949494;
  }
`;

const ErrorLabel = styled.p`
  ${BodyExtraSmallMedium};
  margin-top: 4px;
  color: ${LayoutColors.errorDark};
`;

interface InputProps {
  error: boolean;
}
